import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Check, Minus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAdminDelete, DeleteBar, ConfirmDelete, SelectBox, SelectAll } from "@/components/AdminDelete";

const Tick = ({ on, testid }) => (
  <span data-testid={testid} data-done={on} className={`inline-grid h-6 w-6 place-items-center rounded-full border ${on ? "border-[#E9C98B]/60 bg-[#E9C98B]/10 text-[#E9C98B]" : "border-white/10 text-muted-foreground"}`}>
    {on ? <Check className="h-3.5 w-3.5" /> : <Minus className="h-3 w-3" />}
  </span>
);

const fmt = (d) => (d ? format(new Date(d), "MMM d, yyyy") : "—");

const AddForm = ({ onAdded }) => {
  const [f, setF] = useState({ email: "", shop_name: "", note: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try { await api.post("/admin/invites", f); setF({ email: "", shop_name: "", note: "" }); onAdded(); toast.success("Invitee added. No email was sent."); }
    catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <form onSubmit={submit} data-testid="invite-add-form" className="glass hairline-gold grid gap-2 rounded-2xl border p-4 md:grid-cols-[1.3fr_1fr_1.4fr_auto]">
      <Input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email-input" />
      <Input placeholder="Shop name" value={f.shop_name} onChange={(e) => setF({ ...f, shop_name: e.target.value })} data-testid="invite-shop-input" />
      <Input placeholder="Note (optional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="invite-note-input" />
      <Button type="submit" className="btn-aurora rounded-full" data-testid="invite-add-button">Add invitee</Button>
      {err && <p data-testid="invite-error-message" className="text-sm text-rose-300 md:col-span-4">{err}</p>}
      <p className="text-xs text-muted-foreground md:col-span-4">Tracking only. AetherTags never emails invitees, so invite them personally.</p>
    </form>
  );
};

const STEPS = (i) => [["Signed up", i.signed_up, "signed-up"], ["Shop connected", i.shop_connected, "shop"]];

const Listings = ({ i }) => (
  <div data-testid={`invite-listings-${i.id}`} className="text-xs leading-relaxed">
    <p><span className="text-muted-foreground">Claimed</span> <span className="font-mono" data-testid="invite-claimed">{i.claimed_listings ?? "—"}</span></p>
    <p><span className="text-muted-foreground">Verified</span> <span className="font-mono" data-testid="invite-verified">{i.verified_listings ?? "—"}</span></p>
    {i.mismatch && <span data-testid={`invite-mismatch-${i.id}`} className="mt-1 inline-block rounded-full border border-rose-400/40 bg-rose-400/10 px-2 py-0.5 text-[11px] text-rose-200">Mismatch</span>}
  </div>
);

const SpotToggle = ({ i, onToggle }) => (
  <label className="inline-flex items-center gap-2 text-xs">
    <Switch checked={i.real_spot} onCheckedChange={(v) => onToggle(i, v)} data-testid={`invite-spot-${i.id}`} aria-label="Real-shop spot" />
    <span className={i.real_spot ? "text-[#F3DDAE]" : "text-muted-foreground"}>{i.real_spot ? "Yes" : "No"}</span>
  </label>
);

export const AdminInvites = ({ onChange }) => {
  const [items, setItems] = useState(null);
  const [spotErr, setSpotErr] = useState("");
  const load = useCallback(() => api.get("/admin/invites").then((r) => setItems(r.data.invites)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const del = useAdminDelete("invites", () => { load(); onChange?.(); });
  const remove = (id) => del.ask([id]);
  const toggle = (i, v) => {
    setSpotErr("");
    api.patch(`/admin/invites/${i.id}`, { real_spot: v })
      .then((r) => { toast.success(v ? "Real-shop spot assigned." : r.data.disconnected ? "Spot removed. Their real shop was disconnected and tokens deleted." : "Real-shop spot removed."); load(); })
      .catch((e) => { const m = errMsg(e); setSpotErr(m); toast.error(m); });
  };
  const spots = items?.filter((i) => i.real_spot).length ?? 0;

  return (
    <div data-testid="admin-invites" className="space-y-5">
      <AddForm onAdded={load} />
      <div className="flex flex-wrap items-center gap-3">
        <p data-testid="invite-spots-count" className="rounded-full border border-[#E9C98B]/40 bg-[#E9C98B]/10 px-3 py-1 text-xs text-[#F3DDAE]">Real-shop spots: {spots} of 5 assigned</p>
        {spotErr && <p data-testid="invite-spot-error" className="text-sm text-rose-300">{spotErr}</p>}
      </div>
      <DeleteBar d={del} />
      <ConfirmDelete d={del} />
      <div className="hidden md:block overflow-x-auto rounded-2xl border glass">
        <table className="w-full text-sm" data-testid="admin-invites-table">
          <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="w-10 px-4 py-3"><SelectAll d={del} ids={items?.map((i) => i.id) || []} /></th>{["Invitee", "Status", "Real-shop spot", "Listings", "Invited", "Signed up", "Shop", "Generated", "Approved", "Last active", ""].map((c) => <th key={c} className="px-4 py-3">{c}</th>)}</tr>
          </thead>
          <tbody>
            {items?.map((i) => (
              <tr key={i.id} className="border-t" data-testid={`invite-row-${i.id}`}>
                <td className="px-4 py-3"><SelectBox d={del} id={i.id} /></td>
                <td className="px-4 py-3"><p data-testid="invite-row-email">{i.email}</p><p className="text-xs text-muted-foreground">{i.shop_name}{i.shop_type ? ` · ${i.shop_type}` : ""}{i.note ? ` · ${i.note}` : ""}</p>
                  {i.shop_url && <a href={i.shop_url} target="_blank" rel="noopener noreferrer" data-testid={`invite-open-shop-${i.id}`} className="inline-flex min-h-[44px] items-center text-xs text-[#F3DDAE] underline underline-offset-4">Open shop ↗</a>}</td>
                <td className="px-4 py-3" data-testid="invite-row-status">{i.status}</td>
                <td className="px-4 py-3"><SpotToggle i={i} onToggle={toggle} /></td>
                <td className="px-4 py-3"><Listings i={i} /></td>
                <td className="px-4 py-3"><Tick on testid="invite-row-invited" /></td>
                {STEPS(i).map(([l, on, k]) => <td key={l} className="px-4 py-3"><Tick on={on} testid={`invite-row-${k}`} /></td>)}
                <td className="px-4 py-3 font-mono" data-testid="invite-row-generated">{i.fixes_generated}</td>
                <td className="px-4 py-3 font-mono" data-testid="invite-row-approved">{i.fixes_approved}</td>
                <td className="px-4 py-3 font-mono text-xs" data-testid="invite-row-last-active">{fmt(i.last_active_at)}</td>
                <td className="px-4 py-3"><Button size="icon" variant="ghost" onClick={() => remove(i.id)} data-testid={`invite-remove-${i.id}`} aria-label="Remove invitee"><Trash2 className="h-4 w-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items && !items.length && <p className="p-6 text-sm text-muted-foreground">No invitees yet.</p>}
      </div>
      <ul className="space-y-3 md:hidden" data-testid="admin-invites-mobile">
        {items?.map((i) => (
          <li key={i.id} className="glass rounded-2xl border p-4" data-testid={`invite-card-${i.id}`}>
            <div className="flex items-start justify-between gap-2">
              <span className="pt-0.5"><SelectBox d={del} id={i.id} m /></span>
              <div className="min-w-0 flex-1"><p className="break-all font-medium">{i.email}</p><p className="text-xs text-muted-foreground">{i.shop_name}{i.note ? ` · ${i.note}` : ""}</p></div>
              <Button size="icon" variant="ghost" onClick={() => remove(i.id)} data-testid={`invite-remove-mobile-${i.id}`} aria-label="Remove invitee"><Trash2 className="h-4 w-4" /></Button>
            </div>
            <p className="mt-2 text-sm text-[#F3DDAE]">{i.status}</p>
            {i.shop_url && <a href={i.shop_url} target="_blank" rel="noopener noreferrer" data-testid={`invite-open-shop-mobile-${i.id}`} className="inline-flex min-h-[44px] items-center text-xs text-[#F3DDAE] underline underline-offset-4">Open shop ↗</a>}
            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="text-sm text-muted-foreground">Real-shop spot</span>
              <Switch checked={i.real_spot} onCheckedChange={(v) => toggle(i, v)} data-testid={`invite-spot-mobile-${i.id}`} aria-label="Real-shop spot" />
            </div>
            <div className="mt-2"><Listings i={i} /></div>
            <dl className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Signed up</dt><dd className="text-right"><Tick on={i.signed_up} /></dd>
              <dt className="text-muted-foreground">Shop connected</dt><dd className="text-right"><Tick on={i.shop_connected} /></dd>
              <dt className="text-muted-foreground">Fixes generated</dt><dd className="text-right font-mono">{i.fixes_generated}</dd>
              <dt className="text-muted-foreground">Fixes approved</dt><dd className="text-right font-mono">{i.fixes_approved}</dd>
              <dt className="text-muted-foreground">Last active</dt><dd className="text-right font-mono text-xs">{fmt(i.last_active_at)}</dd>
            </dl>
          </li>
        ))}
        {items && !items.length && <li className="text-sm text-muted-foreground">No invitees yet.</li>}
      </ul>
    </div>
  );
};
